import { useEffect, type ComponentType } from 'react';
import { Navigate } from 'react-router-dom';

import { APP_NAME } from '@/lib/constants';
import { useUser } from '@/hooks/api/use-user';
import { getIsAuthenticated } from '@/lib/storage';
import { useUiStore } from '@/stores/ui-store';

import LargeSpinner from '@/components/shared/spinners/large-spinner';

interface PrivateRouteProps {
    component: ComponentType;
}

/**
 * Garde des routes privées.
 *
 * `isAuthenticated` est le drapeau de session du localStorage : le vrai jeton est dans un cookie
 * HttpOnly, c'est la lecture de l'utilisateur auprès du Pass qui authentifie réellement.
 */
const PrivateRoute = ({ component: Component }: PrivateRouteProps) => {
    const login = useUiStore((state) => state.login);
    const isAuthenticated = getIsAuthenticated();
    const user = useUser();
    const fail = user.isError;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Si pas authentifié et pas de session active, rediriger vers login
    if ((!isAuthenticated && !login) || (fail && isAuthenticated && !login)) {
        localStorage.setItem(`url-${APP_NAME}`, window.location.href);
        return <Navigate to="/login" />;
    }

    // Si échec ou pas authentifié, recharger
    if (fail || !isAuthenticated) {
        window.location.reload();
        return null;
    }

    // Authentifié mais utilisateur pas encore chargé
    if (!login || user.isPending) {
        return <LargeSpinner />;
    }

    return <Component />;
};

export default PrivateRoute;
